import { envConfig } from "@configs/env";
import { EXPERIMENTS, FEATURE_FLAGS, waitForStatsigReadyEvent } from "@configs/statsig";
import { getDevFlags } from "@utils/dev-flags";
import logger from "@utils/logger";
const SERVER_CONFIG_URL = "https://raw.githubusercontent.com/99techteam/kz-config/main/groups";

export const getNotificationDrawerEnabled = async (): Promise<boolean> => {
  const isLocalEnabled = getDevFlags()?.notificationDrawerEnabled;
  if (isLocalEnabled !== null && isLocalEnabled !== undefined) {
    logger._console.log("[Config] Using dev flag for notification drawer", { isLocalEnabled });
    return Boolean(isLocalEnabled);
  }

  try {
    logger._console.log("[Config] Waiting for Statsig readiness (timeout 10s) ...");

    // Get Statsig client from global reference via readiness event (set by AppStatsigProvider)
    const statsigClient = await waitForStatsigReadyEvent();

    if (statsigClient) {
      logger._console.log("[Config] Statsig client ready; checking feature gate", {
        loadingStatus: statsigClient.loadingStatus,
        gate: FEATURE_FLAGS.BOTTOM_NOTIFICATION_DRAWER,
      });
      const bottomNotificationDrawerFeatureGate = statsigClient.getFeatureGate(
        FEATURE_FLAGS.BOTTOM_NOTIFICATION_DRAWER,
      );
      const isGateRecognized = !bottomNotificationDrawerFeatureGate?.details?.reason
        ?.toLowerCase()
        ?.includes("unrecognized");

      const isStatsigBottomNotificationDrawerEnabled = isGateRecognized
        ? bottomNotificationDrawerFeatureGate?.value
        : null;

      logger._console.log("[Config] Feature gate value", {
        gate: FEATURE_FLAGS.BOTTOM_NOTIFICATION_DRAWER,
        value: bottomNotificationDrawerFeatureGate.value,
        isGateRecognized,
      });

      let isStatsigBottomNotificationDrawerEnabledFinal = isStatsigBottomNotificationDrawerEnabled;

      if (isStatsigBottomNotificationDrawerEnabled === true) {
        const experiment = statsigClient.getExperiment(EXPERIMENTS.mvpChromeNotificationBottomDrawer.name);
        const isExperimentRecognized = !experiment?.details?.reason?.toLowerCase()?.includes("unrecognized");

        if (isExperimentRecognized) {
          const bottomNotificationDrawerEnabled = experiment.get(
            EXPERIMENTS.mvpChromeNotificationBottomDrawer.parameters.bottomNotificationDrawerEnabled,
            false,
          );
          logger._console.log("[Config] Using experiment value for notification drawer", {
            experiment: EXPERIMENTS.mvpChromeNotificationBottomDrawer.name,
            value: bottomNotificationDrawerEnabled,
          });
          isStatsigBottomNotificationDrawerEnabledFinal = bottomNotificationDrawerEnabled;
        } else {
          logger._console.log("[Config] Experiment not recognized, using feature gate value", {
            experiment: EXPERIMENTS.mvpChromeNotificationBottomDrawer.name,
          });
        }
      }

      return isStatsigBottomNotificationDrawerEnabledFinal ?? false;
    }
  } catch (error) {
    logger._console.warn("[Config] Statsig not available; falling back to server config", { error });
  }

  logger._console.warn("[Config] Statsig not available; falling back to server config");

  const queryToken = new Date().getTime();

  try {
    const resGroup = await fetch(
      `${SERVER_CONFIG_URL}/${envConfig.brand.group}/${envConfig.deploymentEnv}.json?t=${queryToken}`,
      { cache: "no-store" },
    );
    if (resGroup.ok) {
      const groupConfig = await resGroup.json();
      if (groupConfig?.notificationDrawerEnabled !== undefined) {
        logger._console.log("[Config] Using group server config value for notification drawer", {
          value: groupConfig.notificationDrawerEnabled,
        });
        return Boolean(groupConfig.notificationDrawerEnabled);
      }
    }
  } catch {}

  try {
    const resAccount = await fetch(
      `${SERVER_CONFIG_URL}/${envConfig.brand.group}/${envConfig.accountId}.${envConfig.deploymentEnv}.json?t=${queryToken}`,
      { cache: "no-store" },
    );
    if (resAccount.ok) {
      const accountConfig = await resAccount.json();
      if (accountConfig?.notificationDrawerEnabled !== undefined) {
        logger._console.log("[Config] Using account server config value for notification drawer", {
          value: accountConfig.notificationDrawerEnabled,
        });
        return Boolean(accountConfig.notificationDrawerEnabled);
      }
    }
  } catch {}

  logger._console.log("[Config] Defaulting notification drawer to false");
  return false;
};
